import { useEffect, useState } from 'react';
import { db, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import {
  collectionGroup,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';

type PayoutRow = {
  id: string;
  userId: string;
  amount?: number;
  status?: string;
  mode?: string;
  destinationAddress?: string | null;
  createdAt?: Timestamp;
};

type PayoutActionStatus = 'complete' | 'rejected' | 'failed';

function statusBadgeClass(status: string | undefined): string {
  const s = (status || 'pending').toLowerCase();
  if (s === 'pending') return 'bg-yellow-100 text-yellow-800';
  if (s === 'complete') return 'bg-green-100 text-green-800';
  if (s === 'rejected') return 'bg-red-100 text-red-800';
  if (s === 'failed') return 'bg-gray-100 text-gray-800';
  return 'bg-gray-100 text-gray-800';
}

function formatDateTime(createdAt?: Timestamp): string {
  if (!createdAt || typeof createdAt.toDate !== 'function') return '—';
  try {
    return createdAt.toDate().toLocaleString();
  } catch {
    return '—';
  }
}

function rowKey(row: PayoutRow): string {
  return `${row.userId}-${row.id}`;
}

function actionLabel(status: PayoutActionStatus): string {
  if (status === 'complete') return 'COMPLETE';
  if (status === 'rejected') return 'REJECTED';
  return 'FAILED';
}

export default function Payouts() {
  const [rows, setRows] = useState<PayoutRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingKey, setProcessingKey] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Payouts — Pixano';
  }, []);

  useEffect(() => {
    const q = query(
      collectionGroup(db, 'withdrawals'),
      where('type', '==', 'payout'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data: PayoutRow[] = snapshot.docs.map((docSnap) => {
          const docData = docSnap.data();
          const userId =
            docSnap.ref.parent.parent?.id ?? docData.requestedBy ?? '—';
          return {
            id: docSnap.id,
            userId,
            amount: docData.amount,
            status: docData.status,
            mode: docData.mode,
            destinationAddress: docData.destinationAddress ?? null,
            createdAt: docData.createdAt,
          };
        });
        setRows(data);
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading payouts:', err);
        setError(err.message || 'Failed to load payout requests');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleProcessPayout = async (row: PayoutRow, status: PayoutActionStatus) => {
    const amountLabel = row.amount != null ? `${row.amount} PIX` : 'unknown amount';
    const confirmed = window.confirm(
      `Mark payout of ${amountLabel} for user ${row.userId} as ${actionLabel(status)}?`
    );
    if (!confirmed) return;

    const key = rowKey(row);
    setProcessingKey(key);
    try {
      const processPayout = httpsCallable(functions, 'processPayout');
      await processPayout({
        userId: row.userId,
        payoutId: row.id,
        status,
      });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message)
          : 'Failed to process payout';
      alert(message);
    } finally {
      setProcessingKey(null);
    }
  };

  return (
    <div>
      <div className="border-b mb-4 pb-2">
        <h1 className="text-3xl font-bold mb-4 text-gray-900">Payouts</h1>
        <p className="text-sm text-gray-600">
          PIX withdrawal requests submitted by users in the Pixano app.
        </p>
      </div>

      {error && (
        <div className="p-4 mb-6 bg-red-50 border border-red-200 rounded text-red-700">
          <div className="font-semibold">Error</div>
          <div className="text-sm mt-1">{error}</div>
        </div>
      )}

      <div className="rounded border bg-white overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700 border-b">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-left">Amount</th>
              <th className="px-3 py-2 text-left">Destination</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Mode</th>
              <th className="px-3 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  Loading payout requests...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  No payout requests yet.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const isPending = (row.status || 'pending') === 'pending';
                const isProcessing = processingKey === rowKey(row);

                return (
                  <tr key={rowKey(row)} className="border-t border-gray-200 align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {formatDateTime(row.createdAt)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 font-mono text-xs">
                      {row.userId}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {row.amount != null ? `${row.amount} PIX` : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row.destinationAddress || undefined}>
                      {row.destinationAddress || 'No wallet connected'}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded px-2 py-1 text-xs ${statusBadgeClass(row.status)}`}
                      >
                        {row.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {row.mode || 'manual'}
                    </td>
                    <td className="px-3 py-2">
                      {isPending ? (
                        <div className="flex flex-wrap gap-1">
                          {isProcessing ? (
                            <span className="text-xs text-gray-500">Processing...</span>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="px-2 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                disabled={isProcessing}
                                onClick={() => handleProcessPayout(row, 'complete')}
                              >
                                Mark Complete
                              </button>
                              <button
                                type="button"
                                className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                disabled={isProcessing}
                                onClick={() => handleProcessPayout(row, 'rejected')}
                              >
                                Reject
                              </button>
                              <button
                                type="button"
                                className="px-2 py-1 text-xs rounded bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-50"
                                disabled={isProcessing}
                                onClick={() => handleProcessPayout(row, 'failed')}
                              >
                                Mark Failed
                              </button>
                            </>
                          )}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
